import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Switch } from '@headlessui/react';
import { documentNodePermissionsService, DocumentNodePermissionsDto, PermissionGroupDto, AvailableRolesDto, AvailableTeamsDto, AvailableUsersDto } from '../../../services/documentNodePermissionsService';
import { permissionService } from '../../../services/permissionService';
import { Permission } from '../../../types/permissions';
import { PlusIcon, TrashIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../UI/ToastProvider';

interface DocumentNodePermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  nodeName: string;
  nodePath?: string; // Adicionar path do nó
}

// Interface para compatibilidade com o componente existente
interface PermissionGroup {
  id: string;
  name: string;
  type: 'role' | 'team' | 'user' | 'anonymous';
  permissions: {
    [key: string]: 'allow' | 'deny' | undefined;
  };
}

// Interface para controlar o estado de collapse de cada grupo
interface CollapsedState {
  [groupId: string]: boolean;
}

const DocumentNodePermissionsModal: React.FC<DocumentNodePermissionsModalProps> = ({
  isOpen,
  onClose,
  nodeId,
  nodeName,
  nodePath,
}) => {
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [inheritPermissions, setInheritPermissions] = useState(true);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [availableProfiles, setAvailableProfiles] = useState<PermissionGroup[]>([]);
  const [availableTeams, setAvailableTeams] = useState<PermissionGroup[]>([]);
  const [availableUsers, setAvailableUsers] = useState<PermissionGroup[]>([]);
  const [documentPermissions, setDocumentPermissions] = useState<Permission[]>([]);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [selectedGroupType, setSelectedGroupType] = useState<'role' | 'team' | 'user'>('role');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<PermissionGroup[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [pendingPermissionChange, setPendingPermissionChange] = useState<{
    groupId: string;
    permissionId: string;
    value: 'allow' | 'deny';
  } | null>(null);
  const [showBreakInheritanceModal, setShowBreakInheritanceModal] = useState(false);
  
  // ✅ Novo estado para controlar collapse/expand de cada grupo
  const [collapsedGroups, setCollapsedGroups] = useState<CollapsedState>({});

  // ✅ Verificar se é o documento raiz (sem path ou path vazio)
  const isRootDocument = !nodePath || nodePath === '/' || nodePath === '';
  
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, nodeId]);

  // ✅ Efeito para buscar usuários quando o tipo de grupo muda
  useEffect(() => {
    if (selectedGroupType === 'user') {
      setUserSearchResults([]);
      setUserSearchTerm('');
    }
  }, [selectedGroupType]);

  // ✅ Função para alternar o estado de collapse de um grupo
  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // ✅ Função para obter o nome de exibição de um grupo
  const getGroupDisplayName = (group: PermissionGroup): string => {
    // Se o grupo tem um nome legível, usar ele
    if (group.name && group.name.trim() !== '') {
      return group.name;
    }
    
    // Caso contrário, gerar um nome baseado no tipo e ID
    const shortId = group.id.substring(0, 8);
    switch (group.type) {
      case 'role':
        return `Perfil ${shortId}`;
      case 'team':
        return `Equipe ${shortId}`;
      case 'user':
        return `Usuário ${shortId}`;
      default:
        return `Grupo ${shortId}`;
    }
  };

  // ✅ Função para obter o ícone baseado no tipo de grupo
  const getGroupIcon = (type: string) => {
    switch (type) {
      case 'role':
        return (
          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 text-xs font-medium">P</span>
          </div>
        );
      case 'team':
        return (
          <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-purple-600 text-xs font-medium">E</span>
          </div>
        );
      case 'user':
        return (
          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
            <span className="text-green-600 text-xs font-medium">U</span>
          </div>
        );
      default:
        return (
          <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
            <span className="text-gray-600 text-xs font-medium">G</span>
          </div>
        );
    }
  };

  // ✅ Função para obter a cor de fundo baseada no tipo de grupo
  const getGroupHeaderColor = (type: string): string => {
    switch (type) {
      case 'role':
        return 'bg-blue-50 border-blue-200';
      case 'team':
        return 'bg-purple-50 border-purple-200';
      case 'user':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [permissions, roles, teams, users, documentPermissions] = await Promise.all([
        documentNodePermissionsService.getDocumentNodePermissions(nodeId),
        documentNodePermissionsService.getAvailableRoles(),
        documentNodePermissionsService.getAvailableTeams(),
        documentNodePermissionsService.getAvailableUsers(),
        permissionService.getDocumentPermissions(), // ✅ Buscar permissões de documentos
      ]);

      // Converter DTOs para PermissionGroup
      const convertedRoles: PermissionGroup[] = roles.map(role => ({
        id: role.id,
        name: role.name,
        type: 'role' as const,
        permissions: {} as { [key: string]: 'allow' | 'deny' | undefined }
      }));

      const convertedTeams: PermissionGroup[] = teams.map(team => ({
        id: team.id,
        name: team.name,
        type: 'team' as const,
        permissions: {} as { [key: string]: 'allow' | 'deny' | undefined }
      }));

      const convertedUsers: PermissionGroup[] = users.map(user => ({
        id: user.id,
        name: user.name,
        type: 'user' as const,
        permissions: {} as { [key: string]: 'allow' | 'deny' | undefined }
      }));

      // Para o documento raiz, sempre definir inheritPermissions como false
      if (isRootDocument) {
        setInheritPermissions(false);
      } else {
        setInheritPermissions(permissions.inheritPermissions);
      }
      
      setPermissionGroups(permissions.permissionGroups.map(pg => {
        const convertedPermissions = Object.fromEntries(
          pg.permissions.map(item => [item.permissionId, item.action as 'allow' | 'deny' | undefined])
        );
        
        return {
          id: pg.id,
          name: pg.name,
          type: pg.type as 'role' | 'team' | 'user',
          permissions: convertedPermissions
        };
      }));
      setAvailableProfiles(convertedRoles);
      setAvailableTeams(convertedTeams);
      setAvailableUsers(convertedUsers);
      setDocumentPermissions(documentPermissions); // ✅ Definir permissões disponíveis
      
      // ✅ Inicializar todos os grupos como expandidos por padrão
      const initialCollapsedState: CollapsedState = {};
      permissions.permissionGroups.forEach(pg => {
        initialCollapsedState[pg.id] = false; // false = expandido
      });
      setCollapsedGroups(initialCollapsedState);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Função para buscar usuários com autocomplete
  const searchUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      setIsUserSearching(true);
      // Filtrar usuários disponíveis localmente
      const filteredUsers = availableUsers.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setUserSearchResults(filteredUsers);
      setShowUserDropdown(true);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    } finally {
      setIsUserSearching(false);
    }
  };

  // ✅ Função para lidar com mudança no campo de busca de usuários
  const handleUserSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserSearchTerm(value);
    searchUsers(value);
  };

  // ✅ Função para selecionar um usuário do dropdown
  const handleUserSelect = (user: PermissionGroup) => {
    setUserSearchTerm(user.name);
    setShowUserDropdown(false);
    handleAddGroup(user.id);
  };

  const handlePermissionChange = (groupId: string, permissionId: string, value: 'allow' | 'deny' | undefined) => {
    // Se está herdando permissões e não é documento raiz, solicitar quebra de herança
    if (inheritPermissions && !isRootDocument && value !== undefined) {
      setPendingPermissionChange({ groupId, permissionId, value });
      setShowBreakInheritanceModal(true);
      return;
    }

    // Aplicar a mudança diretamente
    applyPermissionChange(groupId, permissionId, value);
  };

  const applyPermissionChange = (groupId: string, permissionId: string, value: 'allow' | 'deny' | undefined) => {
    setPermissionGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === groupId
          ? {
              ...group,
              permissions: value === undefined 
                ? Object.fromEntries(
                    Object.entries(group.permissions).filter(([key]) => key !== permissionId)
                  )
                : {
                    ...group.permissions,
                    [permissionId]: value,
                  },
            }
          : group
      )
    );
  };

  const handleBreakInheritance = () => {
    if (pendingPermissionChange) {
      setInheritPermissions(false);
      applyPermissionChange(
        pendingPermissionChange.groupId, 
        pendingPermissionChange.permissionId, 
        pendingPermissionChange.value
      );
      setPendingPermissionChange(null);
    }
    setShowBreakInheritanceModal(false);
  };

  const handleCancelBreakInheritance = () => {
    setPendingPermissionChange(null);
    setShowBreakInheritanceModal(false);
  };

  const handleAddGroup = async (groupId: string) => {
    try {
      let group: PermissionGroup | undefined;

      switch (selectedGroupType) {
        case 'role':
          group = availableProfiles.find(p => p.id === groupId);
          break;
        case 'team':
          group = availableTeams.find(t => t.id === groupId);
          break;
        case 'user':
          group = availableUsers.find(u => u.id === groupId) || 
                  userSearchResults.find(u => u.id === groupId);
          break;
      }

      if (group) {
        // ✅ Inicializar permissões vazias para o novo grupo
        const newGroup: PermissionGroup = {
          ...group,
          permissions: {}
        };

        // Adicionar o grupo localmente em vez de chamar o serviço
        setPermissionGroups(prev => [...prev, newGroup]);
        
        // ✅ Inicializar o novo grupo como expandido
        setCollapsedGroups(prev => ({
          ...prev,
          [newGroup.id]: false
        }));
        
        setShowAddGroup(false);
        setUserSearchTerm('');
        setUserSearchResults([]);
        setShowUserDropdown(false);
        
        addToast(`${group.name} adicionado com sucesso!`, 'success');
      }
    } catch (error) {
      console.error('Erro ao adicionar grupo:', error);
      addToast('Erro ao adicionar grupo. Tente novamente.', 'error');
    }
  };

  const handleRemoveGroup = async (groupId: string) => {
    try {
      // Encontrar o grupo antes de remover para mostrar o nome na mensagem
      const groupToRemove = permissionGroups.find(group => group.id === groupId);
      
      // Remover o grupo localmente em vez de chamar o serviço
      setPermissionGroups(prev => prev.filter(group => group.id !== groupId));
      
      if (groupToRemove) {
        addToast(`${groupToRemove.name} removido com sucesso!`, 'success');
      }
    } catch (error) {
      console.error('Erro ao remover grupo:', error);
      addToast('Erro ao remover grupo. Tente novamente.', 'error');
    }
  };

  const handleSave = async () => {
    try {
      
      const permissions: DocumentNodePermissionsDto = {
        documentId: nodeId,
        inheritPermissions,
        permissionGroups: permissionGroups.map(group => ({
          id: group.id,
          name: group.name,
          type: group.type,
          permissions: Object.entries(group.permissions)
            .filter(([_, value]) => value === 'allow' || value === 'deny')
            .map(([permissionId, action]) => ({
              permissionId,
              action: action as string
            }))
        }))
      };
      
      const message = await documentNodePermissionsService.updateDocumentNodePermissions(nodeId, permissions);
      addToast(message, 'success');
      onClose();
    } catch (error) {
      addToast('Erro ao salvar permissões. Tente novamente.', 'error');
    }
  };

  // ✅ Função para obter as opções baseadas no tipo selecionado
  const getAvailableOptions = () => {
    switch (selectedGroupType) {
      case 'role':
        return availableProfiles;
      case 'team':
        return availableTeams;
      case 'user':
        return showUserDropdown ? userSearchResults : availableUsers;
      default:
        return [];
    }
  };

  return (
    <Transition appear show={isOpen} as="div">
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  Permissões: {nodeName}
                </Dialog.Title>

                <div className="mt-4">
                  {/* Ocultar switch de herança para documento raiz */}
                  {!isRootDocument && (
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <Switch
                          checked={inheritPermissions}
                          onChange={setInheritPermissions}
                          className={`${
                            inheritPermissions ? 'bg-blue-600' : 'bg-gray-200'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                        >
                          <span
                            className={`${
                              inheritPermissions ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          Herdar permissões
                        </span>
                      </div>

                      <button
                        type="button"
                        className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${
                          inheritPermissions
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        }`}
                        onClick={() => setShowAddGroup(true)}
                        disabled={inheritPermissions}
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Adicionar Grupo
                      </button>
                    </div>
                  )}

                  {/* Para documento raiz, sempre mostrar o botão de adicionar grupo */}
                  {isRootDocument && (
                    <div className="flex justify-end mb-6">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={() => setShowAddGroup(true)}
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Adicionar Grupo
                      </button>
                    </div>
                  )}

                  {isLoading ? (
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (inheritPermissions && !isRootDocument) ? (
                    <div className="text-center py-8">
                      <div className="text-gray-500 mb-4">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Permissões Herdadas
                      </h3>
                      <p className="text-sm text-gray-500">
                        Este nó está herdando permissões do nó superior. Desabilite a herança para definir permissões específicas.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {permissionGroups.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-4">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {isRootDocument ? 'Nenhuma Permissão Definida' : 'Nenhuma Permissão Definida'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {isRootDocument 
                              ? 'Adicione grupos de permissões para definir quem pode acessar este documento raiz.'
                              : 'Adicione grupos de permissões para definir quem pode acessar este nó.'
                            }
                          </p>
                        </div>
                      ) : (
                        permissionGroups.map(group => (
                          <div key={group.id} className={`border rounded-lg ${getGroupHeaderColor(group.type)}`}>
                            {/* ✅ Header colapsável com ícone e nome intuitivo */}
                            <div 
                              className="flex justify-between items-center p-4 cursor-pointer hover:bg-opacity-75 transition-colors"
                              onClick={() => toggleGroupCollapse(group.id)}
                            >
                              <div className="flex items-center space-x-3">
                                {getGroupIcon(group.type)}
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">
                                    {getGroupDisplayName(group)}
                                  </h4>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {group.type === 'role' ? 'Perfil' : 
                                     group.type === 'team' ? 'Equipe' : 
                                     group.type === 'user' ? 'Usuário' : 'Grupo'}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                {/* ✅ Botão de collapse/expand */}
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGroupCollapse(group.id);
                                  }}
                                >
                                  {collapsedGroups[group.id] ? (
                                    <ChevronRightIcon className="h-5 w-5" />
                                  ) : (
                                    <ChevronDownIcon className="h-5 w-5" />
                                  )}
                                </button>
                                
                                {/* ✅ Botão de remover */}
                                <button
                                  type="button"
                                  className="text-red-600 hover:text-red-800 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveGroup(group.id);
                                  }}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* ✅ Conteúdo colapsável das permissões */}
                            {!collapsedGroups[group.id] && (
                              <div className="px-4 pb-4">
                                <div className="max-h-96 overflow-y-auto">
                                  {documentPermissions.length === 0 ? (
                                    <p className="text-gray-500 text-center py-4">Nenhuma permissão disponível</p>
                                  ) : (
                                    <div className="space-y-3">
                                      {documentPermissions.map(permission => (
                                        <div key={permission.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                                          <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                              <div>
                                                <label className="font-medium text-gray-900 cursor-pointer">
                                                  {permission.name}
                                                </label>
                                                <p className="text-sm text-gray-600">{permission.description}</p>
                                                <div className="flex items-center mt-1">
                                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    permission.module === 'Documentos' || permission.module === 'Conhecimento' 
                                                      ? 'bg-blue-100 text-blue-800' 
                                                      : permission.module === 'Usuários' 
                                                      ? 'bg-green-100 text-green-800'
                                                      : permission.module === 'Equipes'
                                                      ? 'bg-purple-100 text-purple-800'
                                                      : 'bg-gray-100 text-gray-800'
                                                  }`}>
                                                    {permission.module}
                                                  </span>
                                                  <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    permission.type === 'Leitura' ? 'bg-green-100 text-green-800' :
                                                    permission.type === 'Escrita' ? 'bg-yellow-100 text-yellow-800' :
                                                    permission.type === 'Execução' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-red-100 text-red-800'
                                                  }`}>
                                                    {permission.type}
                                                  </span>
                                                </div>
                                              </div>
                                              
                                              {/* Switches para Permitir e Negar */}
                                              <div className="flex items-center space-x-4 ml-4">
                                                <div className="flex items-center">
                                                  <Switch
                                                    checked={group.permissions[permission.id] === 'allow'}
                                                    onChange={(checked) => handlePermissionChange(group.id, permission.id, checked ? 'allow' : undefined)}
                                                    className={`${
                                                      group.permissions[permission.id] === 'allow' ? 'bg-green-600' : 'bg-gray-200'
                                                    } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
                                                  >
                                                    <span
                                                      className={`${
                                                        group.permissions[permission.id] === 'allow' ? 'translate-x-4' : 'translate-x-0'
                                                      } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                                    />
                                                  </Switch>
                                                  <span className="ml-2 text-xs text-green-700 font-medium">Permitir</span>
                                                </div>
                                                
                                                <div className="flex items-center">
                                                  <Switch
                                                    checked={group.permissions[permission.id] === 'deny'}
                                                    onChange={(checked) => handlePermissionChange(group.id, permission.id, checked ? 'deny' : undefined)}
                                                    className={`${
                                                      group.permissions[permission.id] === 'deny' ? 'bg-red-600' : 'bg-gray-200'
                                                    } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2`}
                                                  >
                                                    <span
                                                      className={`${
                                                        group.permissions[permission.id] === 'deny' ? 'translate-x-4' : 'translate-x-0'
                                                      } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                                    />
                                                  </Switch>
                                                  <span className="ml-2 text-xs text-red-700 font-medium">Negar</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {showAddGroup && (!inheritPermissions || isRootDocument) && (
                  <div className="mt-4 p-4 border rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">
                      Adicionar Grupo de Permissões
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Tipo de Grupo
                        </label>
                        <select
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={selectedGroupType}
                          onChange={(e) => setSelectedGroupType(e.target.value as any)}
                        >
                          <option value="role">Perfil</option>
                          <option value="team">Equipe</option>
                          <option value="user">Usuário</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {selectedGroupType === 'user' ? 'Buscar Usuário' : 'Grupo'}
                        </label>
                        
                        {selectedGroupType === 'user' ? (
                          // ✅ Autocomplete para usuários
                          <div className="relative">
                            <div className="relative">
                              <input
                                type="text"
                                className="mt-1 block w-full pl-10 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                placeholder="Digite para buscar usuários..."
                                value={userSearchTerm}
                                onChange={handleUserSearchChange}
                                onFocus={() => setShowUserDropdown(true)}
                              />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                              </div>
                              {isUserSearching && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                </div>
                              )}
                            </div>
                            
                            {/* ✅ Dropdown de resultados */}
                            {showUserDropdown && userSearchResults.length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                {userSearchResults.map((user) => (
                                  <button
                                    key={user.id}
                                    type="button"
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                                    onClick={() => handleUserSelect(user)}
                                  >
                                    <div className="font-medium text-gray-900">{user.name}</div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          // ✅ Select normal para perfis e equipes
                          <select
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            onChange={(e) => handleAddGroup(e.target.value)}
                          >
                            <option value="">Selecione um {selectedGroupType === 'role' ? 'perfil' : 'equipe'}</option>
                            {getAvailableOptions().map(option => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                    onClick={onClose}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleSave}
                  >
                    Salvar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Modal de Quebra de Herança */}
      <Transition appear show={showBreakInheritanceModal} as="div">
        <Dialog as="div" className="relative z-60" onClose={handleCancelBreakInheritance}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Quebrar Herança de Permissões
                  </Dialog.Title>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Este documento está herdando permissões do documento pai. Para definir permissões específicas, 
                      é necessário quebrar a herança. Isso irá:
                    </p>
                    <ul className="mt-3 text-sm text-gray-600 list-disc list-inside">
                      <li>Desabilitar a herança de permissões</li>
                      <li>Permitir definir permissões específicas para este documento</li>
                      <li>Manter as permissões atuais do documento pai como base</li>
                    </ul>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={handleCancelBreakInheritance}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={handleBreakInheritance}
                    >
                      Quebrar Herança
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Transition>
  );
};

export default DocumentNodePermissionsModal; 